import { useCallback, useRef, useState } from 'react';
import { ArrowUp, ImagePlus, Square, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ChatImage } from './types';

function fileToBase64(file: File): Promise<ChatImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve({ data: result.split(',')[1] || '', mediaType: file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface TalkChatComposerProps {
  connected: boolean;
  processing: boolean;
  backendBusy: boolean;
  showLongTaskTip: boolean;
  onSubmit: (payload: { content: string; images: ChatImage[] }) => Promise<boolean>;
  onInterrupt: () => void | Promise<void>;
}

export function TalkChatComposer({
  connected,
  processing,
  backendBusy,
  showLongTaskTip,
  onSubmit,
  onInterrupt,
}: TalkChatComposerProps) {
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState<ChatImage[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeTextarea = useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 160)}px`;
  }, []);

  const addImages = useCallback(async (files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    const images = await Promise.all(imageFiles.map(fileToBase64));
    setPendingImages((prev) => [...prev, ...images]);
  }, []);

  const submitCurrent = useCallback(async () => {
    const content = input.trim();
    const images = [...pendingImages];
    if ((!content && images.length === 0) || processing || backendBusy || !connected) {
      return false;
    }

    const accepted = await onSubmit({ content, images });
    if (!accepted) return false;

    setInput('');
    setPendingImages([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    return true;
  }, [backendBusy, connected, input, onSubmit, pendingImages, processing]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void submitCurrent();
    }
  };

  const handlePaste = useCallback(async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (const item of items) {
      if (!item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (file) imageFiles.push(file);
    }
    if (imageFiles.length === 0) return;
    event.preventDefault();
    await addImages(imageFiles);
  }, [addImages]);

  const canSend = connected && !processing && !backendBusy && (input.trim() || pendingImages.length > 0);

  return (
    <div className="border-t bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      {(processing || backendBusy) && showLongTaskTip ? (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
          Running a longer background task. You can keep waiting or interrupt if the plan changed.
        </div>
      ) : null}

      {pendingImages.length > 0 ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {pendingImages.map((image, index) => (
            <div key={`${image.mediaType}-${index}`} className="group relative">
              <img
                src={`data:${image.mediaType};base64,${image.data}`}
                alt="Pending upload"
                className="h-16 w-16 rounded-lg border object-cover"
              />
              <button
                onClick={() => setPendingImages((prev) => prev.filter((_, currentIndex) => currentIndex !== index))}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="rounded-2xl border bg-muted/30 transition-colors focus-within:border-primary/40">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
            resizeTextarea();
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={processing ? 'Processing...' : 'Talk to the PM agent...'}
          disabled={!connected || processing}
          rows={1}
          className="min-h-[2.75rem] w-full resize-none bg-transparent px-4 pt-3 pb-2 text-sm outline-none placeholder:text-muted-foreground/60 disabled:opacity-50"
          style={{ maxHeight: '160px' }}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => {
            if (event.target.files) void addImages(event.target.files);
            event.target.value = '';
          }}
        />

        <div className="flex items-center justify-between px-3 pb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={!connected || processing}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ImagePlus className="h-4 w-4" />
          </Button>

          {processing || backendBusy ? (
            <Button variant="secondary" size="sm" onClick={() => void onInterrupt()} className="gap-1.5 rounded-full px-3">
              <Square className="h-3.5 w-3.5" />
              Interrupt
            </Button>
          ) : (
            <Button size="icon" onClick={() => void submitCurrent()} disabled={!canSend} className="h-8 w-8 rounded-full">
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
